/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Events CLI", description = "Generate typed SDK wrappers from event contracts so your application code matches the schemas Hightouch expects.", lastUpdatedDate = new Date(1787875200000), keywords = "cli, htevents, typed wrappers, event contracts, codegen";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Events CLI",
        description: "Generate typed SDK wrappers from event contracts so your application code matches the schemas Hightouch expects.",
        lastUpdatedDate: "Fri Aug 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Before you begin",
          "depth": 0,
          "id": "before-you-begin"
        }, {
          "value": "Install the CLI",
          "depth": 0,
          "id": "install-the-cli"
        }, {
          "value": "Create a configuration file",
          "depth": 0,
          "id": "create-a-configuration-file"
        }, {
          "value": "Non-interactive init",
          "depth": 1,
          "id": "non-interactive-init"
        }, {
          "value": "Load contracts from the API",
          "depth": 0,
          "id": "load-contracts-from-the-api"
        }, {
          "value": "Load contracts from Git Sync",
          "depth": 0,
          "id": "load-contracts-from-git-sync"
        }, {
          "value": "Generate wrappers",
          "depth": 0,
          "id": "generate-wrappers"
        }, {
          "value": "Configuration file",
          "depth": 1,
          "id": "configuration-file"
        }, {
          "value": "Call generated wrappers",
          "depth": 0,
          "id": "call-generated-wrappers"
        }, {
          "value": "Keep generated code in sync",
          "depth": 0,
          "id": "keep-generated-code-in-sync"
        }, {
          "value": "Supported SDKs",
          "depth": 0,
          "id": "supported-sdks"
        }, {
          "value": "Commands",
          "depth": 0,
          "id": "commands"
        }, {
          "value": "init",
          "depth": 1,
          "id": "init"
        }, {
          "value": "generate",
          "depth": 1,
          "id": "generate"
        }, {
          "value": "check",
          "depth": 1,
          "id": "check"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "Authentication failed (401)",
          "depth": 1,
          "id": "authentication-failed-401"
        }, {
          "value": "Event source slug was rejected (422)",
          "depth": 1,
          "id": "event-source-slug-was-rejected-422"
        }, {
          "value": "Event domains API returned 404",
          "depth": 1,
          "id": "event-domains-api-returned-404"
        }, {
          "value": "Generated files are out of date",
          "depth": 1,
          "id": "generated-files-are-out-of-date"
        }, {
          "value": "Configuration file contains a token",
          "depth": 1,
          "id": "configuration-file-contains-a-token"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      code: "code",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      pre: "pre",
      span: "span",
      h3: "h3"
    }, _provideComponents(), props.components), {CreateAPIKeySnippet} = _components;
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Engineers instrumenting Hightouch Events"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["An ", _jsx(_components.a, {
                href: "/events/sources/overview",
                children: "event source"
              }), " and at least one ", _jsx(_components.a, {
                href: "/events/contracts/management",
                children: "event contract"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["The Events CLI reads your event contracts and generates typed wrappers around supported Hightouch Events SDKs, such as the ", _jsx(_components.a, {
            href: "/events/sdks/browser",
            children: "Browser"
          }), " and ", _jsx(_components.a, {
            href: "/events/sdks/ios",
            children: "iOS"
          }), " SDKs. You call those wrappers in your app instead of sending untyped track calls."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This page is for the Events CLI (", _jsx(_components.code, {
          children: "htevents"
        }), "). It is a different tool from the ", _jsx(_components.a, {
          href: "/developer-tools/cli-guide",
          children: "Hightouch CLI"
        }), " (", _jsx(_components.code, {
          children: "ht"
        }), "), which manages workspace resources such as models and syncs."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Contracts define the events and fields Hightouch expects. The Events CLI generates typed functions from those contracts. You call the functions from application code."
      }), "\n", _jsx(_components.p, {
        children: "When a contract changes, TypeScript, Python, and other typed languages flag mismatches in your editor. Hightouch still validates events when they arrive. The CLI adds an additional check that happens earlier, in the code you write."
      }), "\n", _jsxs(_components.p, {
        children: ["Use the CLI when you instrument with a Hightouch Events SDK and you want generated helpers that match your contracts. Skip it if you send events only through the ", _jsx(_components.a, {
          href: "/events/sdks/http",
          children: "HTTP API"
        }), " or a streaming source such as Kafka."]
      }), "\n", _jsx(_components.p, {
        children: "The CLI never installs or constructs the Events SDK — you still initialize the SDK yourself. Generated files only wrap it."
      }), "\n", _jsx(_components.h2, {
        id: "before-you-begin",
        children: "Before you begin"
      }), "\n", _jsx(_components.p, {
        children: "Before you generate wrappers, make sure you have:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Node.js 18 or later, which includes ", _jsx(_components.code, {
            children: "npm"
          }), " and ", _jsx(_components.code, {
            children: "npx"
          })]
        }), "\n", _jsx(_components.li, {
          children: "An event source in Hightouch"
        }), "\n", _jsx(_components.li, {
          children: "At least one event contract in a domain attached to that source"
        }), "\n", _jsx(_components.li, {
          children: "The Events SDK for your language, installed in the same project"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Copy the source slug from the event source. Go to ", _jsx(_components.strong, {
          children: "Event Collection > Event sources"
        }), ", open the source, and click the ", _jsx(_components.strong, {
          children: "Setup"
        }), " tab. The ", _jsx(_components.strong, {
          children: "Slug"
        }), " field identifies this source in Git Sync YAML and the Events CLI. It is not the write key."]
      }), "\n", _jsx(_components.h2, {
        id: "install-the-cli",
        children: "Install the CLI"
      }), "\n", _jsx(_components.p, {
        children: "Install the CLI as a development dependency:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "npm install --save-dev @ht-sdks/events-cli\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Run it from the project directory with ", _jsx(_components.code, {
          children: "npx"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["npx htevents --", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "help"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A local install does not put ", _jsx(_components.code, {
          children: "htevents"
        }), " on your ", _jsx(_components.code, {
          children: "PATH"
        }), "; ", _jsx(_components.code, {
          children: "npx"
        }), " resolves the copy in ", _jsx(_components.code, {
          children: "node_modules"
        }), ". To put it on your ", _jsx(_components.code, {
          children: "PATH"
        }), ", install globally with ", _jsx(_components.code, {
          children: "npm install -g @ht-sdks/events-cli"
        }), ". Prefer the local install above so the CLI version stays pinned in CI and shared project environments."]
      }), "\n", _jsxs(_components.p, {
        children: ["If your repo has no ", _jsx(_components.code, {
          children: "package.json"
        }), " (for example, a Swift or Android app), ", _jsx(_components.code, {
          children: "npm install --save-dev"
        }), " will create one. You can also run the CLI without adding a dependency using:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["npx @ht-sdks/events-cli --", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "help"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "create-a-configuration-file",
        children: "Create a configuration file"
      }), "\n", _jsx(_components.p, {
        children: "From your application repository, run:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "npx htevents init\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "init"
        }), " asks for:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The event source slug"
        }), "\n", _jsxs(_components.li, {
          children: ["Whether to load contracts from the Hightouch API or a local ", _jsx(_components.a, {
            href: "/extensions/git-sync#event-contract-schema",
            children: "Git Sync"
          }), " checkout"]
        }), "\n", _jsx(_components.li, {
          children: "Which SDK to generate for"
        }), "\n", _jsx(_components.li, {
          children: "Where to write the generated files"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["It writes ", _jsx(_components.code, {
          children: "htevents.config.json"
        }), ". Commit that file. Do not put API tokens in it."]
      }), "\n", _jsx(_components.h3, {
        id: "non-interactive-init",
        children: "Non-interactive init"
      }), "\n", _jsx(_components.p, {
        children: "In CI or scripts, pass every value as a flag:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["npx htevents init \\\n  --", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "source"
          }), " web-app \\\n  --input api \\\n  --sdk browser-ts \\\n  --output ./src/analytics/generated.ts\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "--input git-sync"
        }), " also requires ", _jsx(_components.code, {
          children: "--git-sync-path"
        }), ". Use ", _jsx(_components.code, {
          children: "--force"
        }), " to overwrite an existing configuration file."]
      }), "\n", _jsx(_components.h2, {
        id: "load-contracts-from-the-api",
        children: "Load contracts from the API"
      }), "\n", _jsx(_components.p, {
        children: "API input fetches the contracts attached to your event source from Hightouch."
      }), "\n", _jsx(_components.p, {
        children: "Create an API key as a workspace Admin:"
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsxs(_components.p, {
        children: ["API keys authenticate as the user who created them. See ", _jsx(_components.a, {
          href: "/developer-tools/api-guide",
          children: "API overview"
        }), " for permissions and what happens if that user loses access."]
      }), "\n", _jsxs(_components.p, {
        children: ["Pass the key as ", _jsx(_components.code, {
          children: "HIGHTOUCH_API_TOKEN"
        }), " or ", _jsx(_components.code, {
          children: "--token"
        }), ". ", _jsx(_components.code, {
          children: "--token"
        }), " wins when both are set."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: [_jsx(_components.span, {
            className: "hljs-built_in",
            children: "export"
          }), " HIGHTOUCH_API_TOKEN=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_API_KEY\""
          }), "\nnpx htevents generate\n"]
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Never store the API key in ", _jsx(_components.code, {
            children: "htevents.config.json"
          }), ". The CLI rejects a\nconfiguration file that contains ", _jsx(_components.code, {
            children: "token"
          }), ", ", _jsx(_components.code, {
            children: "apiKey"
          }), ", or ", _jsx(_components.code, {
            children: "api_key"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "load-contracts-from-git-sync",
        children: "Load contracts from Git Sync"
      }), "\n", _jsxs(_components.p, {
        children: ["If your workspace ", _jsx(_components.a, {
          href: "/extensions/git-sync#event-contract-schema",
          children: "version-controls event contracts"
        }), ", you can generate from a local checkout instead of the API. No API key is required."]
      }), "\n", _jsxs(_components.p, {
        children: ["Set ", _jsx(_components.code, {
          children: "input"
        }), " to ", _jsx(_components.code, {
          children: "git-sync"
        }), " and point ", _jsx(_components.code, {
          children: "path"
        }), " at the Git Sync repository root or its ", _jsx(_components.code, {
          children: "events"
        }), " directory:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"web-app\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"input\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"git-sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"path\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./events\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"outputs\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sdk\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"browser-ts\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"path\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./src/analytics/generated.ts\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The CLI reads ", _jsx(_components.code, {
          children: "events/domains"
        }), " (current layout) or ", _jsx(_components.code, {
          children: "events/contracts"
        }), " (legacy layout). Don't mix both layouts in the same repository."]
      }), "\n", _jsx(_components.h2, {
        id: "generate-wrappers",
        children: "Generate wrappers"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "npx htevents generate\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "generate"
        }), " writes typed wrappers to each path in ", _jsx(_components.code, {
          children: "outputs"
        }), " and writes ", _jsx(_components.code, {
          children: "htevents.lock.json"
        }), " next to the configuration file. The lock file records which contract versions produced the code."]
      }), "\n", _jsxs(_components.p, {
        children: ["Do not edit generated files. When contracts change, run ", _jsx(_components.code, {
          children: "generate"
        }), " again."]
      }), "\n", _jsxs(_components.p, {
        children: ["Commit ", _jsx(_components.code, {
          children: "htevents.config.json"
        }), ". You can commit the generated sources and ", _jsx(_components.code, {
          children: "htevents.lock.json"
        }), ", or add them to ", _jsx(_components.code, {
          children: ".gitignore"
        }), " and run ", _jsx(_components.code, {
          children: "generate"
        }), " after clone and in CI."]
      }), "\n", _jsxs(_components.p, {
        children: ["Committing generated files lets ", _jsx(_components.code, {
          children: "htevents check"
        }), " fail a PR when contracts changed but wrappers were not regenerated. If you ignore them, run ", _jsx(_components.code, {
          children: "generate"
        }), " in CI before you compile instead of ", _jsx(_components.code, {
          children: "check"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "configuration-file",
        children: "Configuration file"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "htevents.config.json"
        }), " looks like this:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"$schema\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./node_modules/@ht-sdks/events-cli/schemas/config.schema.json\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"web-app\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"input\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"api\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"outputs\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sdk\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"browser-ts\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"path\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./src/analytics/generated.ts\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "What it does"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "$schema"
                })
              })
            }), _jsx(_components.td, {
              children: "Optional. Points at the JSON Schema shipped with the package so editors can validate the file."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "source"
                })
              })
            }), _jsxs(_components.td, {
              children: ["Event source slug from the ", _jsx(_components.strong, {
                children: "Setup"
              }), " tab."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "input"
                })
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "api"
              }), " or ", _jsx(_components.code, {
                children: "git-sync"
              }), ". Git Sync also needs ", _jsx(_components.code, {
                children: "path"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "outputs"
                })
              })
            }), _jsxs(_components.td, {
              children: ["One or more ", _jsx(_components.code, {
                children: "{ \"sdk\", \"path\" }"
              }), " entries. Paths are relative to the configuration file."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["To generate for more than one SDK, add another ", _jsx(_components.code, {
          children: "outputs"
        }), " entry:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"outputs\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sdk\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"browser-ts\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"path\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./src/analytics/generated.ts\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sdk\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"node-ts\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"path\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./server/analytics/generated.ts\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["PHP writes several files. Set ", _jsx(_components.code, {
          children: "path"
        }), " to a directory, not a single file."]
      }), "\n", _jsxs(_components.p, {
        children: ["Pass ", _jsx(_components.code, {
          children: "-c"
        }), " / ", _jsx(_components.code, {
          children: "--config"
        }), " when the file is not ", _jsx(_components.code, {
          children: "./htevents.config.json"
        }), ". ", _jsx(_components.code, {
          children: "generate"
        }), " and ", _jsx(_components.code, {
          children: "check"
        }), " resolve paths from that file's directory, not from the current working directory."]
      }), "\n", _jsx(_components.h2, {
        id: "call-generated-wrappers",
        children: "Call generated wrappers"
      }), "\n", _jsx(_components.p, {
        children: "Install and initialize the Events SDK the same way you would without the CLI. Then import the generated helpers."
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.code, {
          children: "browser-ts"
        }), ", bind the SDK instance once, then call a wrapper for each event:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-jsx",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEventsBrowser"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"@ht-sdks/events-sdk-js-browser\""
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { setHtEvents, trackOrderCompleted } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./src/analytics/generated\""
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " analytics = ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEventsBrowser"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "load"
          }), "(\n  { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "writeKey"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"WRITE_KEY\""
          }), " },\n  { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "apiHost"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"us-east-1.hightouch-events.com\""
          }), " },\n);\n", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "setHtEvents"
          }), "(analytics);\n\n", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "trackOrderCompleted"
          }), "({ ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "orderId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"abc-123\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "total"
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "49.99"
          }), " });\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Replace ", _jsx(_components.code, {
          children: "WRITE_KEY"
        }), " and ", _jsx(_components.code, {
          children: "apiHost"
        }), " with the values from the event source ", _jsx(_components.strong, {
          children: "Setup"
        }), " tab. See the ", _jsx(_components.a, {
          href: "/events/sdks/browser",
          children: "Browser SDK"
        }), " for initialization options."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each contract version becomes a named wrapper. ", _jsx(_components.code, {
          children: "Order Completed"
        }), " version ", _jsx(_components.code, {
          children: "v2"
        }), " becomes ", _jsx(_components.code, {
          children: "trackOrderCompletedV2"
        }), " in TypeScript, or ", _jsx(_components.code, {
          children: "track_order_completed_v2"
        }), " in Python and Ruby."]
      }), "\n", _jsxs(_components.p, {
        children: ["The latest version of each event also gets an unversioned alias, such as ", _jsx(_components.code, {
          children: "trackOrderCompleted"
        }), ". Wrappers inject the schema version on the event so Hightouch validates against the matching contract. You do not need to set ", _jsx(_components.code, {
          children: "context.htevents.schemaVersion"
        }), " yourself."]
      }), "\n", _jsxs(_components.p, {
        children: ["Other SDKs follow the same pattern. Some languages take the SDK client as an argument instead of calling ", _jsx(_components.code, {
          children: "setHtEvents"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "keep-generated-code-in-sync",
        children: "Keep generated code in sync"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "htevents check"
        }), " rebuilds the expected files in memory and compares them to disk. It exits ", _jsx(_components.code, {
          children: "0"
        }), " when they match. It exits ", _jsx(_components.code, {
          children: "2"
        }), " when a generated file is missing or different:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Generated files are out of date:\n  src/analytics/generated.ts\nRun `htevents generate` to update.\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If generated files are committed, run ", _jsx(_components.code, {
          children: "check"
        }), " in CI so a contract change cannot land without regenerating wrappers:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "run:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "npx"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "htevents"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "check"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "env:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "HIGHTOUCH_API_TOKEN:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "${{"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "secrets.HIGHTOUCH_API_TOKEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "}}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If generated files are gitignored, run ", _jsx(_components.code, {
          children: "generate"
        }), " in CI before you compile. ", _jsx(_components.code, {
          children: "check"
        }), " always reports drift when those files are missing."]
      }), "\n", _jsx(_components.p, {
        children: "If you generate from Git Sync, check out that repository in CI and omit the token."
      }), "\n", _jsx(_components.h2, {
        id: "supported-sdks",
        children: "Supported SDKs"
      }), "\n", _jsxs(_components.p, {
        children: ["Set ", _jsx(_components.code, {
          children: "outputs[].sdk"
        }), " to one of these identifiers. Install the matching Events SDK in the same project."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "CLI identifier"
            }), _jsx(_components.th, {
              children: "Events SDK"
            }), _jsx(_components.th, {
              children: "SDK docs"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "browser-ts"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "@ht-sdks/events-sdk-js-browser"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/browser",
                children: "Browser"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node-ts"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "@ht-sdks/events-sdk-js-node"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/nodejs",
                children: "Node.js"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "python"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "events-sdk-python"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/python",
                children: "Python"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ruby"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "events-sdk-ruby"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/ruby",
                children: "Ruby"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "php"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht-sdks/events-sdk-php"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/php",
                children: "PHP"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "csharp"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Hightouch.Events.CSharp"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/csharp",
                children: "C# (.NET)"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "go"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "github.com/ht-sdks/events-sdk-go"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/go",
                children: "Go"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "swift"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "events-sdk-swift"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/ios",
                children: "iOS"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "android"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "com.hightouch.analytics.android:analytics"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/android",
                children: "Android"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "react-native"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "@ht-sdks/events-sdk-react-native"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/react-native",
                children: "React Native"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flutter"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "@ht-sdks/events-sdk-flutter"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/flutter",
                children: "Flutter"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "kotlin"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "com.github.ht-sdks.events-sdk-kotlin:core"
              })
            }), _jsx(_components.td, {
              children: "Coming soon"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "java"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "com.github.ht-sdks.events-sdk-java:analytics"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/sdks/java",
                children: "Java"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Generated files record the minimum peer SDK version they were tested against."
      }), "\n", _jsx(_components.h2, {
        id: "commands",
        children: "Commands"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "htevents [options] [command]\n"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Global options"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "-c, --config <path>"
              })
            }), _jsxs(_components.td, {
              children: ["Path to the configuration file. Default: ", _jsx(_components.code, {
                children: "./htevents.config.json"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--token <token>"
              })
            }), _jsxs(_components.td, {
              children: ["Workspace API token. Overrides ", _jsx(_components.code, {
                children: "HIGHTOUCH_API_TOKEN"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--debug"
              })
            }), _jsx(_components.td, {
              children: "Print stack traces for errors."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "-V, --version"
              })
            }), _jsx(_components.td, {
              children: "Print the CLI version."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "init",
        children: "init"
      }), "\n", _jsxs(_components.p, {
        children: ["Creates ", _jsx(_components.code, {
          children: "htevents.config.json"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--source <slug>"
              })
            }), _jsx(_components.td, {
              children: "Event source slug."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--input <type>"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "api"
              }), " or ", _jsx(_components.code, {
                children: "git-sync"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--git-sync-path <path>"
              })
            }), _jsxs(_components.td, {
              children: ["Local Git Sync directory. Required with ", _jsx(_components.code, {
                children: "--input git-sync"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--sdk <sdk>"
              })
            }), _jsxs(_components.td, {
              children: ["Target SDK. Default: ", _jsx(_components.code, {
                children: "browser-ts"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--output <path>"
              })
            }), _jsx(_components.td, {
              children: "Output path for generated code."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "--force"
              })
            }), _jsx(_components.td, {
              children: "Overwrite an existing configuration file."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "generate",
        children: "generate"
      }), "\n", _jsxs(_components.p, {
        children: ["Fetches contracts and writes typed wrappers plus ", _jsx(_components.code, {
          children: "htevents.lock.json"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "check",
        children: "check"
      }), "\n", _jsxs(_components.p, {
        children: ["Verifies generated files match the current contracts. Exit code ", _jsx(_components.code, {
          children: "2"
        }), " means they are out of date."]
      }), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "authentication-failed-401",
        children: "Authentication failed (401)"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " ", _jsx(_components.code, {
          children: "--token"
        }), " or ", _jsx(_components.code, {
          children: "HIGHTOUCH_API_TOKEN"
        }), " is missing, expired, or not a workspace API key."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Create a new API key as an Admin and pass it to ", _jsx(_components.code, {
          children: "generate"
        }), " or ", _jsx(_components.code, {
          children: "check"
        }), ". See ", _jsx(_components.a, {
          href: "/developer-tools/api-guide",
          children: "API overview"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "event-source-slug-was-rejected-422",
        children: "Event source slug was rejected (422)"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The ", _jsx(_components.code, {
          children: "source"
        }), " value in the configuration file is not a valid event source slug."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Copy the ", _jsx(_components.strong, {
          children: "Slug"
        }), " from the event source ", _jsx(_components.strong, {
          children: "Setup"
        }), " tab. Do not use the write key or the source name."]
      }), "\n", _jsx(_components.h3, {
        id: "event-domains-api-returned-404",
        children: "Event domains API returned 404"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The API key's workspace cannot list event domains."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Confirm you can open ", _jsx(_components.strong, {
          children: "Event Collection > Contracts"
        }), " in that workspace. Confirm the API key belongs to the same workspace as the event source."]
      }), "\n", _jsx(_components.h3, {
        id: "generated-files-are-out-of-date",
        children: "Generated files are out of date"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Contracts changed, or generated files were edited by hand."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Run ", _jsx(_components.code, {
          children: "htevents generate"
        }), " and commit the updated files."]
      }), "\n", _jsx(_components.h3, {
        id: "configuration-file-contains-a-token",
        children: "Configuration file contains a token"
      }), "\n", _jsxs(_components.p, {
        children: ["You may see this error if ", _jsx(_components.code, {
          children: "htevents.config.json"
        }), " includes a secret:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Config must not contain \"token\". Pass the API token via --token or the HIGHTOUCH_API_TOKEN environment variable.\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The configuration file includes ", _jsx(_components.code, {
          children: "token"
        }), ", ", _jsx(_components.code, {
          children: "apiKey"
        }), ", or ", _jsx(_components.code, {
          children: "api_key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Resolution:"
        }), " Remove the secret from the file. Pass it with ", _jsx(_components.code, {
          children: "--token"
        }), " or ", _jsx(_components.code, {
          children: "HIGHTOUCH_API_TOKEN"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Define or update ", _jsx(_components.a, {
            href: "/events/contracts/management",
            children: "event contracts"
          }), " for the events you generate wrappers for."]
        }), "\n", _jsxs(_components.li, {
          children: ["Review ", _jsx(_components.a, {
            href: "/events/contracts/violations",
            children: "how Hightouch handles contract violations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Initialize the Events SDK for your platform — for example ", _jsx(_components.a, {
            href: "/events/sdks/browser",
            children: "Browser"
          }), " or ", _jsx(_components.a, {
            href: "/events/sdks/ios",
            children: "iOS"
          }), "."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
